import type {
  Attachment,
  Message,
  Participant,
  Presence,
  Room,
} from "@catmail/shared";

import type { AvatarDotTone } from "@/components";

/**
 * 방 하나를 화면에 어떻게 보여줄지 정하는 순수 함수들.
 * 컴포넌트가 아니라 여기 모아두면 목록·헤더·프로필이 같은 답을 낸다.
 */

/**
 * 도메인 상태를 아바타의 표현 용어로 옮긴다.
 *
 * **같은 서버 사람은 추측하지 않는다.** `status`는 메일에 접속이라는 게 없어서
 * 대화가 굴러가는 모양으로 지어낸 값인데(computeStatus), 그 사람이 지금 붙어
 * 있는지 실제로 알 때는 그쪽이 맞다. 아는 것이 있는데 추측을 보여줄 이유가 없다.
 */
export function avatarToneOf(
  peer: Pick<Participant, "status" | "presence">,
): AvatarDotTone {
  if (peer.presence) return presenceToneOf(peer.presence);

  switch (peer.status) {
    case "active":
      return "positive";
    case "awaiting":
      return "pending";
    case "idle":
      return "neutral";
    case "noreply":
      return "negative";
  }
}

function presenceToneOf(presence: Presence): AvatarDotTone {
  switch (presence) {
    case "online":
      return "positive";
    case "away":
      return "pending";
    case "offline":
      return "neutral";
  }
}

/** 나를 뺀 참여자들. 방의 정체성은 이쪽이다. */
export function peersOf(room: Room): Participant[] {
  return room.participants.filter((participant) => !participant.isSelf);
}

/**
 * 목록과 헤더에 뜨는 방 이름.
 * 직접 붙인 이름이 있으면 그것, 없으면 참여자 이름을 잇는다.
 */
export function roomTitleOf(room: Room): string {
  if (room.title) return room.title;

  const peers = peersOf(room);
  if (peers.length === 0) return "나에게 쓴 메모";
  if (peers.length === 1) return peers[0]?.displayName ?? "(이름 없음)";

  return peers.map((peer) => peer.displayName).join(", ");
}

/**
 * 입력창에 흐리게 뜨는 말. 디스코드의 "#일반에 메시지 보내기"와 같은 자리다.
 *
 * "메시지 입력…"은 칸이 하는 일만 말하고 **누구에게 쓰는 중인지**는 말하지
 * 않는다. 방을 잘못 열어둔 채 쓰기 시작해도 알 방법이 없는데, 여기서는 그게
 * 진짜 메일 한 통이라 되돌릴 수 없다.
 *
 * 조사는 사람이면 "에게", 내가 붙인 방 이름이면 "에"를 쓴다.
 */
export function composerPlaceholderFor(room: Room, narrow = false): string {
  /*
   * 좁은 화면에서는 이름을 뺀다.
   *
   * 한 줄에 안 들어가기 때문이다. 390px에서 입력칸에 남는 폭은 190px 남짓인데
   * "에게 메시지 보내기"만 해도 그 절반을 먹는다. 이름까지 붙이면 두 줄로 접히고,
   * 그러면 아무것도 안 썼는데 입력칸이 두 줄 높이로 벌어진다.
   * (`text-overflow`는 `::placeholder`에 먹지 않아서 …로 줄일 수도 없다.)
   *
   * 잃는 것도 별로 없다. 폰에서는 대화가 한 번에 하나만 보이고 바로 위 헤더에
   * 이름이 큼직하게 있다. 여러 칸이 나란히 보이는 넓은 화면에서만 값을 한다.
   */
  if (narrow) return "메시지 보내기";

  const peers = peersOf(room);
  if (peers.length === 0) return "나에게 메시지 보내기";

  // 직접 붙인 방 이름은 사람 이름이 아니다 — "디자인팀에게"는 어색하다.
  if (room.title) return `${room.title}에 메시지 보내기`;

  return `${roomTitleOf(room)}에게 메시지 보내기`;
}

/**
 * 방을 대표하는 아바타 한 명. 그룹방은 첫 참여자를 쓰되,
 * 목록에서는 이름 이니셜(예: "디자")을 쓰도록 별도 처리한다.
 */
export function roomLeadOf(room: Room): Participant | null {
  return peersOf(room)[0] ?? null;
}

/** 헤더 두 번째 줄. 1:1이면 메일 주소, 그룹이면 참여자 이름들. */
export function roomSubtitleOf(room: Room): string {
  const peers = peersOf(room);

  if (room.kind === "group") {
    return [...peers.map((peer) => peer.displayName), "나"].join(", ");
  }

  const lead = peers[0];
  if (!lead) return "";

  return room.isQuiet ? `${lead.address} · 조용한 방` : lead.address;
}

/**
 * 이 방에 답장을 쓸 수 있는가.
 * 상대가 전부 발신전용이면 보낼 곳이 없다 — 시안 04의 "답장할 수 없어요".
 */
export function canReplyTo(room: Room): boolean {
  const peers = peersOf(room);
  if (peers.length === 0) return true;

  return peers.some((peer) => peer.status !== "noreply");
}

/**
 * "주고받은 파일"로 셀 첨부.
 *
 * 인라인 이미지(`cid:`)는 **본문의 일부**다. 광고 메일 한 통에 로고·배너·아이콘이
 * 예닐곱 개씩 박혀 오는데, 그것을 파일로 세면 말풍선 아래에 정체 모를 첨부 카드가
 * 줄줄이 붙고 "받은 파일 7개" 같은 숫자가 뜬다. 사용자가 주고받았다고 느끼는
 * 파일이 아니다 — 그림은 원문 화면에서 본문 안에 그려진다.
 */
export function filesOf(message: Pick<Message, "attachments">): Attachment[] {
  return message.attachments.filter((attachment) => !attachment.inline);
}

/** 여러 메시지에 걸친 파일. 방 하나의 "주고받은 파일"이 이것이다. */
export function filesIn(messages: readonly Message[]): Attachment[] {
  return messages.flatMap(filesOf);
}

/** 그룹방 목록 아바타에 쓸 이니셜. 방 이름이 있으면 그걸 줄인다. */
export function roomInitialsOf(room: Room): string {
  const lead = roomLeadOf(room);
  if (!room.title) return lead?.initials ?? "?";

  return room.title.slice(0, 2);
}
